import { AmbientLight, DirectionalLight, Mesh, PlaneGeometry, ShadowMaterial, type Scene } from 'three';

type TCardDimensions = { cardH: number; cardW: number };

const CARD_SHADOW_NAME = 'cardShadow';

// Ambient + fill + rim + key DirectionalLights + shadow floor. Fill
// and rim contribute normalMap contrast (ambient alone can't carve
// bumps) — without them the paper texture reads as flat.
class CardLightingRig {
    private _ambient: AmbientLight | null = null;
    private _backLight: DirectionalLight | null = null;
    private _fillLight: DirectionalLight | null = null;
    private _keyLight: DirectionalLight | null = null;
    private _rimLight: DirectionalLight | null = null;
    private _shadowFloor: Mesh | null = null;

    attach(scene: Scene, dimensions: TCardDimensions): void {
        const { cardH, cardW } = dimensions;

        this._ambient = new AmbientLight(0xffffff, 2.7);
        scene.add(this._ambient);

        // Positions offset from head-on so the paper normalMap catches
        // grazing angles — light hitting the surface too frontally
        // gives flat shading with no visible bumps.
        this._fillLight = new DirectionalLight(0xf0f4ff, 0.3);
        this._fillLight.position.set(-4, 1.5, 0.3);
        scene.add(this._fillLight);

        this._rimLight = new DirectionalLight(0xffffff, 0.2);
        this._rimLight.position.set(1.5, -0.5, -3);
        scene.add(this._rimLight);

        // Back key light — mirror of keyLight in Z so the verso face
        // gets equivalent grazing illumination when the user orbits
        // behind the card. Same intensity for parity, offset in X so
        // the bump highlights don't repeat symmetrically.
        this._backLight = new DirectionalLight(0xffffff, 0.75);
        this._backLight.position.set(-cardW * 1.5, cardH * 2.4, -cardH * 0.6);
        this._backLight.target.position.set(0, cardH / 2, 0);
        scene.add(this._backLight);
        scene.add(this._backLight.target);

        const keyLight = new DirectionalLight(0xffffff, 0.75);
        keyLight.position.set(cardW * 1.8, cardH * 2.4, cardH * 0.6);
        keyLight.target.position.set(0, cardH / 2, 0);
        keyLight.castShadow = true;
        keyLight.shadow.mapSize.set(2048, 2048);
        keyLight.shadow.radius = 20;
        keyLight.shadow.blurSamples = 30;
        keyLight.shadow.camera.near = 0.1;
        keyLight.shadow.camera.far = cardH * 10;
        keyLight.shadow.camera.left = -cardW * 2;
        keyLight.shadow.camera.right = cardW * 2;
        keyLight.shadow.camera.top = cardH * 3;
        keyLight.shadow.camera.bottom = -cardH * 2;
        scene.add(keyLight);
        scene.add(keyLight.target);
        this._keyLight = keyLight;

        this._shadowFloor = new Mesh(new PlaneGeometry(cardW * 6, cardH * 6), new ShadowMaterial({ opacity: 0.15 }));
        this._shadowFloor.name = CARD_SHADOW_NAME;
        this._shadowFloor.rotation.x = -Math.PI / 2;
        this._shadowFloor.receiveShadow = true;
        scene.add(this._shadowFloor);
    }

    detach(scene: Scene): void {
        if (this._ambient) {
            scene.remove(this._ambient);
            this._ambient = null;
        }
        if (this._fillLight) {
            scene.remove(this._fillLight);
            this._fillLight = null;
        }
        if (this._rimLight) {
            scene.remove(this._rimLight);
            this._rimLight = null;
        }
        if (this._backLight) {
            scene.remove(this._backLight);
            scene.remove(this._backLight.target);
            this._backLight = null;
        }
        if (this._keyLight) {
            scene.remove(this._keyLight);
            scene.remove(this._keyLight.target);
            this._keyLight = null;
        }
        if (this._shadowFloor) {
            scene.remove(this._shadowFloor);
            this._shadowFloor.geometry.dispose();
            (this._shadowFloor.material as ShadowMaterial).dispose();
            this._shadowFloor = null;
        }
    }
}

export { CardLightingRig };
export type { TCardDimensions };
